import React from "react";
import { liveOnlineExamDetails, assessmentPrinciples } from "../mock/mockData";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Video, Users, Shield, Calendar, MessageSquare, Headphones, CheckCircle2 } from "lucide-react";
import { SideIllustration } from "../components/common/SideIllustration";
import { IMAGE_SOURCES } from "../mock/imageSources";

const featureIcons = {
  "Real-Time Performance": Video,
  "Direct Examiner Interaction": Users,
  "Secure Video Platform": Shield,
  "Flexible Scheduling": Calendar,
  "Immediate Feedback": MessageSquare,
  "Technical Support": Headphones,
};

export default function ExaminationSystem() {
  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Examination System</h1>
        <p className="max-w-3xl text-sm leading-relaxed text-slate-700 sm:text-base">
          The Global Music Conservatory conducts all examinations through our Live Online Examination system, providing a rigorous, standardised assessment experience that maintains the highest quality standards while offering accessibility to candidates worldwide.
        </p>
      </section>

      {/* Live Online Examination Hero Section */}
      <section className="grid gap-6 rounded-2xl border border-[#c9a227]/40 bg-gradient-to-br from-[#fffdf5] to-[#f9f2e6] p-5 shadow-sm md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] md:p-6">
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-bold tracking-tight text-[#182033]">{liveOnlineExamDetails.title}</h2>
            <p className="text-sm font-medium text-[#8c7648]">{liveOnlineExamDetails.subtitle}</p>
          </div>
          <p className="text-sm leading-relaxed text-[#4b4a46]">
            {liveOnlineExamDetails.overview}
          </p>
        </div>
        <SideIllustration
          src={IMAGE_SOURCES.examPianoStudent}
          alt="Candidate preparing a performance for an online music examination"
        />
      </section>

      {/* Key Features */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold tracking-tight text-slate-900">Key Features</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {liveOnlineExamDetails.features.map((feature) => {
            const IconComponent = featureIcons[feature.title] || Video;
            return (
              <Card
                key={feature.title}
                className="border-slate-200 bg-white/80 shadow-sm backdrop-blur-xl"
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#182033] text-[#d5c39d]">
                      <IconComponent className="h-4 w-4" />
                    </span>
                    <CardTitle className="text-sm font-semibold text-slate-900">
                      {feature.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-700">{feature.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Examination Process */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold tracking-tight text-slate-900">Examination Process</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {liveOnlineExamDetails.process.map((step) => (
            <div
              key={step.step}
              className="relative rounded-2xl border border-slate-200 bg-white/80 p-5 shadow-sm backdrop-blur-xl"
            >
              <div className="absolute -top-3 left-4 inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#182033] text-sm font-bold text-white">
                {step.step}
              </div>
              <div className="mt-2 space-y-2">
                <h3 className="text-sm font-semibold text-slate-900">{step.title}</h3>
                <p className="text-xs leading-relaxed text-slate-600">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Technical Requirements */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold tracking-tight text-slate-900">Technical Requirements</h2>
        <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5 shadow-sm backdrop-blur-xl sm:p-6">
          <p className="mb-4 text-sm text-slate-700">
            To ensure a smooth examination experience, please ensure you meet the following requirements before your scheduled examination:
          </p>
          <ul className="grid gap-3 sm:grid-cols-2">
            {liveOnlineExamDetails.requirements.map((req, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-green-600" />
                <span>{req}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Components Assessed */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold tracking-tight text-slate-900">Components Assessed</h2>
        <div className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50/80 p-5 shadow-sm backdrop-blur-xl sm:grid-cols-2 sm:p-6">
          <div className="space-y-2 text-sm text-slate-700">
            <p>
              Each instrument syllabus sets out the components that may be assessed at each level. These typically include performance of set or own-choice repertoire, technical work such as scales or studies, and additional musicianship elements where appropriate.
            </p>
            <p>
              Detailed examination requirements, including timings and repertoire lists where applicable, are provided in the full syllabus documentation for teachers and candidates.
            </p>
          </div>
          <div className="space-y-2 text-sm text-slate-700">
            <p>
              Examiners consider accuracy, fluency, tonal control, stylistic awareness and overall communication. Weightings between components are clearly indicated so that candidates understand how marks are allocated.
            </p>
          </div>
        </div>
      </section>

      {/* Assessment Principles */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold tracking-tight text-slate-900">Assessment Principles</h2>
        <ul className="grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
          {assessmentPrinciples.map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1 h-1.5 w-1.5 flex-none rounded-full bg-[#c9a227]" aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
